import React from 'react';
import makeStyles from '@mui/styles/makeStyles';

import Card from 'spectra-logic-ui/components/card';
import Table from 'spectra-logic-ui/components/table';
import StorageIcon from 'spectra-logic-ui/icons/Storage';

import {getTitle} from '@/title';
import {Pool} from '@/types';
import ButtonToolbar from '@/components/button_toolbar';
import CardHeader from '@/components/card_header';
import DeleteDialog from '@/components/delete_dialog';
import AddCloudStorageWizard from '@/storage/form/add/cloud_storage';
import EditStorageWizard from '@/storage/form/edit';
import DeleteStorageWizard from '@/storage/form/delete';
import StorageRows from '@/storage/storage_rows';
import {StorageRowId} from '@/storage/types';
import {isCloud} from '@/storage/cloud';
import DisableIcon from 'spectra-logic-ui/icons/Block';
import EnableIcon from 'spectra-logic-ui/icons/Check';
import DisableStorageDialog from '@/storage/form/disable';

type Props = {
  selectedPool?: Pool;
  selectedRowId: StorageRowId;
  selectRowId: Function;
  clearSelectedRow: () => any;
  setOpenDetails: Function;
  pools: Pool[];
  fetchingPools: boolean;
}

const useStyles = makeStyles({
  nowrap: {'whiteSpace': 'nowrap'},
});

const CloudStorage = ({
  selectedPool, selectedRowId, selectRowId, clearSelectedRow, setOpenDetails, pools, fetchingPools,
}: Props) => {
  const cloudPools = pools.filter((p) => isCloud(p));
  const deleteDialogDescription = 'Are you sure you want to delete this empty storage?';
  const canEditOrDelete = selectedPool && isCloud(selectedPool);

  const classes = useStyles();

  const tooltip = 'Any storage added will be available for use with ' + getTitle() + ' lifecycle rules.';

  let disableStorageDialogDisableState = true;
  if (selectedPool && selectedPool.disabled) {
    disableStorageDialogDisableState = false;
  }

  // StorageRows is only rendered when 'cloudPools.length > 0' so 'No items to display'
  // is shown if there are no cloud pools.
  return (
    <React.Fragment>
      <Card>
        <CardHeader icon={StorageIcon} tooltip={tooltip}>
          Cloud Storage
        </CardHeader>
        <Card.Body sx={{position: 'relative'}}>
          <ButtonToolbar sx={{position: 'sticky', top: 80}}>
            <ButtonToolbar.CreateDialogButton title='Add' dialog={AddCloudStorageWizard} onClick={clearSelectedRow} />
            <ButtonToolbar.EditDialogButton
              disabled={!canEditOrDelete}
              dialog={(props: any) => <EditStorageWizard pool={selectedPool} {...props} />}
              onSuccess={clearSelectedRow}
            />
            <ButtonToolbar.DeleteDialogButton
              disabled={!canEditOrDelete}
              dialog={(props: any) => {
                if (selectedPool) {
                  if (selectedPool.empty) {
                    return <DeleteDialog title='Delete Cloud Storage' id={selectedPool.id}
                      name={selectedPool.name} description={deleteDialogDescription}
                      resource='storage' {...props}/>;
                  } else {
                    return <DeleteStorageWizard pool={selectedPool} {...props} />;
                  }
                }
                return null;
              }}
              onSuccess={clearSelectedRow}
            />
            <ButtonToolbar.DialogButton
              icon={disableStorageDialogDisableState ? DisableIcon : EnableIcon}
              title={disableStorageDialogDisableState ? 'Disable' : 'Enable'} color='primary'
              disabled={!canEditOrDelete}
              dialog={(props: any) => {
                return <DisableStorageDialog pool={selectedPool} disable={disableStorageDialogDisableState}
                  {...props} />;
              }}
              onSuccess={clearSelectedRow}
            />
          </ButtonToolbar>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Cell>Name</Table.Cell>
                <Table.Cell className={classes.nowrap}>Type</Table.Cell>
                <Table.Cell className={classes.nowrap}>Storage Class</Table.Cell>
                <Table.Cell className={classes.nowrap}>Status</Table.Cell>
                <Table.Cell></Table.Cell>
              </Table.Row>
            </Table.Header>
            <Table.Body isLoading={fetchingPools}>
              {cloudPools.length > 0 ?
                <StorageRows pools={cloudPools} selectRowId={selectRowId} selectedRowId={selectedRowId}
                  setOpenDetails={() => setOpenDetails(true)}/> : null}
            </Table.Body>
          </Table>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default CloudStorage;
